<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur=""
      opacity="1"
      rounded="sm"
      :show="loading"
    >
      <b-card>
        <b-card-title>
          <h4>ETA Logs</h4>
        </b-card-title>

        <!-- Action Buttons -->
        <b-row class="mb-3">
          <b-col>
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              @click="refreshLogs"
            >
              <feather-icon icon="RefreshCwIcon" />
            </b-button>
          </b-col>
        </b-row>

        <!-- ETA Logs Table -->
        <b-table
          responsive
          :items="tableData"
          :fields="tableColumns"
          show-empty
          empty-text="No ETA logs found"
          class="mt-2"
        >
          <template #cell(createdAt)="data">
            {{ formatDate(data.item.createdAt) }}
          </template>

          <template #cell(user_id)="data">
            <span v-if="data.item.attributes && data.item.attributes.user_id">
              {{ data.item.attributes.user_id }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(rideFare)="data">
            <span v-if="data.item.attributes && data.item.attributes.rideFare !== undefined">
              {{ data.item.attributes.rideFare }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(dropoff_zone)="data">
            <span v-if="data.item.attributes && data.item.attributes.dropoff_zone">
              {{ data.item.attributes.dropoff_zone }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(pickupCoordinates)="data">
            <span v-if="data.item.attributes && data.item.attributes.pickupLatitude && data.item.attributes.pickupLongitude">
              {{ data.item.attributes.pickupLatitude }}, {{ data.item.attributes.pickupLongitude }}
            </span>
            <span v-else>-</span>
          </template>

          <template #cell(dropoffCoordinates)="data">
            <span v-if="data.item.attributes && data.item.attributes.dropoffLatitude && data.item.attributes.dropoffLongitude">
              {{ data.item.attributes.dropoffLatitude }}, {{ data.item.attributes.dropoffLongitude }}
            </span>
            <span v-else>-</span>
          </template>

        </b-table>

        <!-- Pagination -->
        <base-pagination
          v-model="queryParams.page"
          :total="total"
          @new-per-page="queryParams.perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BTable,
  BOverlay,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import {
  reactive,
  ref,
  computed,
  watch,
  toRef,
  onMounted,
} from '@vue/composition-api'
import BasePagination from '@/UI/BasePagination.vue'
import axiosIns from '@/libs/axios'
import { errorNotification } from '@/utils/toast-notifications'
import formatDate from '@/utils/date-format'

export default {
  components: {
    BCard,
    BCardTitle,
    BTable,
    BOverlay,
    BButton,
    BRow,
    BCol,
    BasePagination,
  },
  setup() {
    const loading = ref(false)
    const etaLogsData = ref([])
    const total = ref(0)

    const queryParams = reactive({
      page: 1,
      perPage: 10,
    })

    const tableColumns = [
      {
        key: 'pickupCoordinates',
        label: 'Pickup Location',
      },
      {
        key: 'dropoffCoordinates',
        label: 'Dropoff Location',
      },
      {
        key: 'createdAt',
        label: 'Timestamp',
        sortable: true,
      },
      {
        key: 'rideFare',
        label: 'Price Estimate',
      },
      {
        key: 'userId',
        label: 'User ID',
      },
    ]

    const tableData = computed(() => etaLogsData.value)

    const fetchEtaLogs = async () => {
      try {
        loading.value = true
        const response = await axiosIns.get('/users/eta-log', { params: queryParams })
        console.log(response)
        etaLogsData.value = response.data.data.data.etas || []
        total.value = response.data.data.data.total || 0
      } catch (error) {
        console.error('Error fetching ETA logs:', error)
        errorNotification('Failed to fetch ETA logs')
        etaLogsData.value = []
        total.value = 0
      } finally {
        loading.value = false
      }
    }

    const refreshLogs = () => {
      fetchEtaLogs()
    }

    // Watch for pagination changes
    watch([toRef(queryParams, 'page'), toRef(queryParams, 'perPage')], () => {
      fetchEtaLogs()
    })

    // Initial fetch
    onMounted(() => {
      fetchEtaLogs()
    })

    return {
      loading,
      queryParams,
      tableColumns,
      tableData,
      total,
      fetchEtaLogs,
      refreshLogs,
      formatDate,
    }
  },
}
</script>

<style scoped>
.btn-icon {
  height: 2.214rem;
  width: 2.214rem;
}
</style>
